import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CuratedPhotoStrip } from "@/pages/curated/CuratedPhotoStrip";
import { SERVICE_TYPES, uid, addDays } from "@/pages/curated/shared";
import { fmtDayChipLong } from "@/pages/cotizador/shared";

export function CuratedItineraryTab({ trip, onChange }) {
  const days = trip.days || [];
  const addDay = () => {
    const last = days[days.length - 1];
    const date = last?.date ? addDays(last.date, 1) : (trip.start_date || null);
    onChange({ days: [...days, { id: uid("day"), day_number: days.length + 1, date, city: last?.city || "", title: "", services: [] }] });
  };
  const updateDay = (id, patch) => onChange({ days: days.map((d) => (d.id === id ? { ...d, ...patch } : d)) });
  const removeDay = (id) => onChange({ days: days.filter((d) => d.id !== id).map((d, i) => ({ ...d, day_number: i + 1 })) });

  return (
    <div className="space-y-4" data-testid="curated-itinerary-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{days.length} día{days.length !== 1 ? "s" : ""} en el itinerario</p>
        <Button onClick={addDay} size="sm" data-testid="curated-add-day"><Plus className="mr-1.5 h-4 w-4" /> Añadir día</Button>
      </div>
      {days.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="curated-days-empty">Aún no hay días. Empieza añadiendo el primero.</div>
      ) : days.map((d, idx) => (
        <DayEditor key={d.id} day={d} index={idx} destination={trip.destination} onUpdate={(p) => updateDay(d.id, p)} onRemove={() => removeDay(d.id)} />
      ))}
    </div>
  );
}

function DayEditor({ day, index, destination, onUpdate, onRemove }) {
  const services = day.services || [];
  const addService = () => onUpdate({ services: [...services, { id: uid("svc"), type: "actividad", name: "", city: day.city || "", time: "", duration: "", sales_description: "", images: [] }] });
  const updateService = (id, patch) => onUpdate({ services: services.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const removeService = (id) => onUpdate({ services: services.filter((s) => s.id !== id) });
  return (
    <Card data-testid={`curated-day-${index}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2 border-b pb-3">
          <span className="w-14 shrink-0 text-xs font-semibold uppercase tracking-wider text-primary">Día {index + 1}</span>
          <Input type="date" value={day.date || ""} onChange={(e) => onUpdate({ date: e.target.value })} className="h-8 w-40" data-testid={`curated-day-date-${index}`} />
          {day.date && <span className="text-xs text-muted-foreground">{fmtDayChipLong(day.date)}</span>}
          <Input value={day.city || ""} onChange={(e) => onUpdate({ city: e.target.value })} placeholder="Ciudad" className="h-8 w-40" data-testid={`curated-day-city-${index}`} />
          <Input value={day.title || ""} onChange={(e) => onUpdate({ title: e.target.value })} placeholder="Título del día (ej: Welcome to Piedmont)" className="h-8 flex-1" data-testid={`curated-day-title-${index}`} />
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onRemove} aria-label="Eliminar día" data-testid={`curated-day-remove-${index}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
        {services.map((s, j) => (
          <div key={s.id} className="rounded-md border bg-muted/30 p-3" data-testid={`curated-service-${index}-${j}`}>
            <div className="grid gap-2 sm:grid-cols-[130px_1fr_90px_100px_32px]">
              <Select value={s.type || "actividad"} onValueChange={(v) => updateService(s.id, { type: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={s.name || ""} onChange={(e) => updateService(s.id, { name: e.target.value })} placeholder="Nombre de la actividad" className="h-8" data-testid={`curated-service-name-${index}-${j}`} />
              <Input value={s.time || ""} onChange={(e) => updateService(s.id, { time: e.target.value })} placeholder="Hora" className="h-8" />
              <Input value={s.duration || ""} onChange={(e) => updateService(s.id, { duration: e.target.value })} placeholder="Duración" className="h-8" />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeService(s.id)} aria-label="Quitar" data-testid={`curated-service-remove-${index}-${j}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
            <Textarea rows={2} value={s.sales_description || ""} onChange={(e) => updateService(s.id, { sales_description: e.target.value })} placeholder="Descripción para el cliente (opcional)" className="mt-2 text-sm" />
            <div className="mt-2"><CuratedPhotoStrip images={s.images || []} onChange={(imgs) => updateService(s.id, { images: imgs })} query={s.name || day.city || destination} testid={`curated-service-photos-${index}-${j}`} /></div>
          </div>
        ))}
        <Button variant="outline" size="sm" className="w-full border-dashed" onClick={addService} data-testid={`curated-add-service-${index}`}><Plus className="mr-1.5 h-4 w-4" /> Añadir actividad</Button>
      </CardContent>
    </Card>
  );
}
